import { useNavigate } from "react-router-dom";
import type { PodcastSearchResult, Subscription } from "@/types/podcast";
import { prefetchPodcastFeed } from "@/services/podcastApi";

type PodcastLike = PodcastSearchResult | Subscription;

// Downsize iTunes artwork URLs (e.g., /600x600bb.jpg → /200x200bb.jpg)
function getOptimizedArtwork(url: string): string {
  if (!url) return url;
  return url.replace(/\/\d+x\d+bb\.(jpg|png)$/i, "/200x200bb.$1");
}

function isPodcastSearchResult(p: PodcastLike): p is PodcastSearchResult {
  return "itunes_id" in p;
}

function getTitle(p: PodcastLike): string {
  return isPodcastSearchResult(p) ? p.title : p.podcast_name;
}

function getFeedUrl(p: PodcastLike): string | null {
  return p.feed_url;
}

function getArtwork(p: PodcastLike): string {
  return isPodcastSearchResult(p) ? p.artwork_url : p.artwork_url;
}

function getAuthor(p: PodcastLike): string {
  return p.author;
}

interface PodcastCardProps {
  podcast: PodcastLike;
  /** Compact mode for horizontal carousel */
  compact?: boolean;
}

export default function PodcastCard({ podcast, compact }: PodcastCardProps) {
  const navigate = useNavigate();
  const feedUrl = getFeedUrl(podcast);
  const isSpotifyExclusive = !feedUrl;

  function handleClick() {
    if (!feedUrl) return;
    navigate(`/browse/podcast?feed=${encodeURIComponent(feedUrl)}`);
  }

  function handleMouseEnter() {
    if (feedUrl) prefetchPodcastFeed(feedUrl);
  }

  return (
    <button
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      disabled={isSpotifyExclusive}
      className={`group flex flex-col items-center rounded-lg border border-gray-200 bg-white text-left transition-shadow ${
        compact ? "p-2" : "p-3"
      } ${
        isSpotifyExclusive
          ? "cursor-not-allowed opacity-60"
          : "hover:shadow-md"
      }`}
    >
      <div className={`relative aspect-square w-full overflow-hidden rounded-md bg-gray-100 ${compact ? "mb-2" : "mb-3"}`}>
        {getArtwork(podcast) ? (
          <img
            src={getOptimizedArtwork(getArtwork(podcast))}
            alt={getTitle(podcast)}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-300">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className={compact ? "h-8 w-8" : "h-12 w-12"}
            >
              <path d="M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .456.122Z" />
            </svg>
          </div>
        )}
        {isSpotifyExclusive && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <span className={`rounded-full bg-black/70 px-2 py-0.5 font-medium text-white ${compact ? "text-[10px]" : "text-xs"}`}>
              Spotify Only
            </span>
          </div>
        )}
      </div>
      <p className={`w-full truncate font-medium text-gray-900 ${compact ? "text-xs" : "text-sm"}`}>
        {getTitle(podcast)}
      </p>
      {!compact && (
        <>
          <p className="w-full truncate text-xs text-gray-500">
            {getAuthor(podcast)}
          </p>
          {isPodcastSearchResult(podcast) && podcast.episode_count > 0 && (
            <p className="mt-0.5 w-full text-xs text-gray-400">
              {podcast.episode_count} episodes
            </p>
          )}
        </>
      )}
    </button>
  );
}
